import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Gavel, IdCard } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import type { AdminAppeal } from '@/lib/adapters'
import { fetchDecidedAppeals, BAN_STR as S } from './strikesApi'

/**
 * A8.12 — Apellyatsiya qarori (jurnal). JONLI `/admin/appeals` ustidan hal
 * qilingan qarorlar (status !== pending). Har qator → apellyatsiya kartasi
 * (/appeals/:id, A8.11). Backend `548626f`.
 */
export function AppealDecisionsScreen() {
  const navigate = useNavigate()

  const columns = useMemo<AdminColumn<AdminAppeal>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 70 },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
      {
        key: 'user_id',
        header: S.userId,
        type: 'text',
        width: 160,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'kind',
        header: S.kind,
        type: 'enum',
        width: 110,
        options: [
          { value: 'ban', label: S.kindBan, status: 'banned' },
          { value: 'strike', label: S.kindStrike, status: 'review' },
        ],
      },
      {
        key: 'status',
        header: S.action,
        type: 'enum',
        width: 140,
        options: [
          { value: 'granted', label: S.granted, status: 'active' },
          { value: 'rejected', label: S.rejected, status: 'deleted' },
        ],
      },
      { key: 'decision_note', header: S.decisionNote, type: 'longtext', minWidth: 200 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AdminAppeal>[]>(
    () => [
      {
        id: 'open',
        label: S.open,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/appeals/${r.id}`),
      },
    ],
    [navigate],
  )

  const fetcher = useCallback(fetchDecidedAppeals, [])
  return (
    <>
      <PageHeader title={S.decisions} screenId="A8.12" description={S.decisionsNote} />
      <DataTable<AdminAppeal>
        screenId="A8.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Gavel size={28} strokeWidth={1.5} />, title: S.decisions }}
      />
    </>
  )
}
